// --- Dependencies -------------------------------------------------------------------------------
import { useRef } from "react";
import { useLocation } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useNavTabs from "@/hooks/useNavTabs";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import Avatar from "@/UI/atoms/Avatar";
import ProfilePopup from "@/UI/popups/ProfilePopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
/**
 * The title and the account chip, and nothing else.
 *
 * There was a global search box here and it is gone: **searching is a section's own job**, because
 * what a search means depends on the list it runs against — a phone number in the people section, a
 * company name in the companies one. One box above all of them had to guess which, and a search that
 * guesses is slower than one that does not.
 */
export default function AppBar() {
    const t = useTranslation();
    const { pathname } = useLocation();
    const { tabs, footerTabs } = useNavTabs();
    const admin = useAuth((state) => state.admin);
    const addComp = usePopup((state) => state.addComp);
    const chipRef = useRef<HTMLButtonElement>(null);

    // The title is the tab's own label, so it is translated once and can never drift from the
    // sidebar. Every group has to be in here: one left out falls through to the account title.
    const known = [...tabs, ...footerTabs];
    const title = known.find((tab) => (tab.path === "/" ? pathname === "/" : pathname.startsWith(tab.path)))?.label ?? t.profile.myProfile;

    const openProfile = () => {
        const rect = chipRef.current?.getBoundingClientRect();
        if (!rect) return;
        addComp({
            name: "profile",
            Component: ProfilePopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            anchor: { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height },
        });
    };

    return (
        <header className="app-bar">
            <h1 className="app-bar__title">{title}</h1>

            <span className="app-bar__divider" />

            <button ref={chipRef} type="button" className="app-bar__user" onClick={openProfile} aria-label={t.profile.myProfile}>
                <Avatar name={admin?.name ?? ""} picture={admin?.picture} size={28} bordered />
                <span className="app-bar__user-name">{admin?.name}</span>
            </button>
        </header>
    );
}
